import { useIonModal } from '@ionic/react'
import { AnimatePresence, motion } from 'framer-motion'

import { useManagementGetNotes } from '@apps-common/apis/management/note'
import {
  ManagementNoteListCard,
  ManagementNoteListCardSkeleton,
  ManagementNoteModal
} from '@apps-common/apps/portal/pages/units/pages/manage/pages/notes/components'
import { NoResultsAddCard, PullRefresh } from '@apps-common/components/common'
import { LayoutPadding } from '@apps-common/components/layout'
import { ModalDismiss } from '@apps-common/config/modal'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { sortNumerically } from '@apps-common/helpers/array-sort'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'

interface IProps {
  entityId: string
}

export const ManagementNotes: React.FC<IProps> = ({ entityId }) => {
  const { data, isLoading, isError, isRefetching, refetch } = useManagementGetNotes({
    config: {
      enabled: entityId !== undefined
    },
    entityId
  })

  const [present, dismiss] = useIonModal(ManagementNoteModal, {
    componentProps: {
      entityId
    },
    onDismiss: () => {
      dismiss(null, ModalDismiss.Confirm)
    },
    enableBackdropDismiss: true
  })

  const loading = isLoading || isError

  return (
    <>
      <PullRefresh loading={isRefetching} callback={() => refetch()} />
      <LayoutPadding classes='pt-[60px] md:pt-[70px]'>
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-[10px]'>
          <AnimatePresence mode='popLayout'>
            {loading &&
              Array.from(Array(4).keys()).map((n, i) => (
                <motion.div key={`management-notes-skeleton-${i}`} {...motionFadeIn(0.2)}>
                  <ManagementNoteListCardSkeleton />
                </motion.div>
              ))}
            {!loading && data && data?.length === 0 && (
              <motion.div key='no-management-notes-create-button' {...motionFadeIn(0.25)}>
                <NoResultsAddCard
                  title='No notes'
                  message='Add a new note to device.'
                  disabled={loading}
                  callback={() =>
                    present({
                      enterAnimation: modalSlideUpEnterAnimation,
                      leaveAnimation: modalSlideUpLeaveAnimation,
                      backdropDismiss: true
                    })
                  }
                />
              </motion.div>
            )}
            {!loading &&
              data &&
              data?.length > 0 &&
              data
                ?.sort((a, b) => sortNumerically(a, b, 'portalNoteId'))
                .map((n, i) => (
                  <motion.div key={`management-notes-${n.noteId}-${i}`} {...motionFadeIn(0.2)}>
                    <ManagementNoteListCard note={n} />
                  </motion.div>
                ))}
          </AnimatePresence>
        </div>
      </LayoutPadding>
    </>
  )
}
